import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, from, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Document } from '../../models/document';
import { DocumentResponse, normalizeDocumentResponse } from '../../models/documentResponse';
import { ErrorResponse } from '../../models/sdk-interfaces';
import { AutoclassifierSdkService } from '../../services/autoclassifier-sdk/autoclassifier-sdk.service';
import { DocumentManagementService } from '../../services/document-management/document-management.service';
import { AuthService } from '../auth/auth.service';

export interface MergeRequest {
  documentIds: string[];
  outputFileName: string;
}

export interface SplitRequest {
  documentId: string;
  pageNumbers: number[];
  newDocumentFileName: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  errors: string[];
  [key: string]: any;
}

export interface MergeResponse extends ApiResponse<any> {
  documentId: string;
  blobStorageId: string;
}
export interface SplitResponse extends ApiResponse<any> {
  splitDocumentResults: {
    splitDocumentResult: Array<{
      type: string;
      documentId: string;
      blobStorageId: string;
    }>;
  };
}
@Injectable({
  providedIn: 'root'
})
export class DocumentModificationService {
  private http = inject(HttpClient);
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private docMngmtService = inject(DocumentManagementService);
  private authService = inject(AuthService);

  private readonly apiUrl: string = environment.urls.modificationApi;

  /**
   * Get the headers needed for API requests
   * @param tenantId The tenant ID to include in the request headers
   */
  private getHeaders(tenantId: string): HttpHeaders {
    const authKey = localStorage.getItem('authKey') || '';

    return new HttpHeaders({
      'Content-Type': 'application/json',
      tenantId: tenantId
    });
  }

  /**
   * Merge multiple documents into a single PDF
   * @param tenantId The tenant ID
   * @param request The merge request data
   */
  mergeDocuments(tenantId: string, request: MergeRequest): Observable<MergeResponse> {
    const url = `${this.apiUrl}/api/merge`;
    const headers = this.getHeaders(tenantId);
    return this.http.post<MergeResponse>(url, request, { headers }).pipe(
      map(response => {
        return response;
      }),
      catchError(error => {
        console.error('Error merging documents:', error);
        return throwError(() => new Error(this.getErrorMessage(error)));
      })
    );
  }

  /**
   * Split a document into multiple documents based on selected pages
   * @param tenantId The tenant ID
   * @param request The split request data
   */
  splitDocument(tenantId: string, request: SplitRequest): Observable<SplitResponse> {
    const url = `${this.apiUrl}/api/split`;
    const headers = this.getHeaders(tenantId);
    return this.http.post<SplitResponse>(url, request, { headers }).pipe(
      catchError(error => {
        console.error('Error splitting document:', error);
        return throwError(() => new Error(this.getErrorMessage(error)));
      })
    );
  }

  /**
   * Retrieves the document from the Document Management SDK
   * @param tenantId The tenant ID to use for the request
   * @param documentId - The ID of the document to retrieve
   * @returns Observable<Document> - The document with its content loaded
   */
  getDocumentWithContent(tenantId: string, documentId: string): Observable<Document> {
    const authToken = this.authService.getAuthToken() || '';
    return from<Promise<DocumentResponse>>(
      this.autoclassifierSdkService.documentManagementSdk.getDocument(
        tenantId,
        documentId,
        'contentmetadata',
        authToken
      )
    ).pipe(
      switchMap((response: DocumentResponse) => {
        const normalizedResponse = normalizeDocumentResponse(response);
        const newDocument: Document = {
          contentUri: normalizedResponse.contentUri,
          fileName: normalizedResponse.fileName,
          fileId: normalizedResponse.id,
          contentType: normalizedResponse.type,
          fileContent: '',
          completed: false,
          classificationStarted: true
        };

        return this.docMngmtService.getDocument(newDocument.contentUri).pipe(
          map(arrayBuffer => {
            const binaryString = Array.from(new Uint8Array(arrayBuffer))
              .map(byte => String.fromCharCode(byte))
              .join('');
            const base64 = btoa(binaryString);
            newDocument.fileContent = base64;

            return newDocument;
          })
        );
      }),
      catchError((error: ErrorResponse) => {
        console.error('Document get Error:', error);
        return throwError(
          () => new Error(`Failed to load document: ${error.errorMessage ?? 'Unknown error'}`)
        );
      })
    );
  }

  /**
   * Extract a meaningful error message from the error response
   * @param error The error object returned from the HTTP request
   */
  private getErrorMessage(error: any): string {
    if (error.error) {
      if (error.error.errors && error.error.errors.length > 0) {
        return error.error.errors.join(', ');
      }
      if (error.error.message) {
        return error.error.message;
      }
      if (typeof error.error === 'string') {
        return error.error;
      }
    }

    return error.message || error.statusText || 'Unknown error occurred';
  }
}
